import { CommonModule, Location } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnInit, Output, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { LetDirective } from '@ngrx/component';
import { Store } from '@ngrx/store';
import { PatientProvider } from '../../../../care-team/models/patient-provider';
import { getPatientCareTeam } from '../../../../care-team/store/actions/care-team.actions';
import { selectCareTeam, selectIsLoading } from '../../../../care-team/store/selectors/care-team.selector';
import { loadPageWithPatientIdParameter } from '../../../../core/store/actions/app.actions';
import { selectIsMobileBrowser } from '../../../../core/store/selectors/app.selector';
import { EmptyStateComponent } from '../../../../shared/components/empty-state/empty-state.component';
import { IconComponent } from '../../../../shared/components/icon/icon.component';
import { PageHeaderComponent } from '../../../../shared/components/page-header/page-header.component';
import { ProgressSpinnerComponent } from '../../../../shared/components/progress-spinner/progress-spinner.component';

@Component({
  selector: 'app-provider-selection',
  imports: [
    CommonModule,
    LetDirective,
    IconComponent,
    EmptyStateComponent,
    MatButtonModule,
    ProgressSpinnerComponent,
    PageHeaderComponent,
  ],
  templateUrl: './provider-selection.component.html',
  styleUrl: './provider-selection.component.scss',
})
export class ProviderSelectionComponent implements OnInit {
  private store = inject(Store);
  private location = inject(Location);
  @Input() isModal: boolean = false;
  @Output() providerUserIdSelected = new EventEmitter<string>();

  careTeam$ = this.store.select(selectCareTeam);
  isLoading$ = this.store.select(selectIsLoading);
  isMobileBrowser = this.store.selectSignal(selectIsMobileBrowser);
  searchText = signal('');
  selectedProviderUserId = signal<string | null>(null);

  ngOnInit(): void {
    this.store.dispatch(getPatientCareTeam());
  }

  filterCareTeam(list: PatientProvider[] | null | undefined): PatientProvider[] {
    const q = this.searchText().trim().toLowerCase();
    if (!list || list.length === 0) return [];
    if (!q) return list;

    return list.filter(provider => {
      const name = (provider.displayName ?? '').toLowerCase();
      const team = (this.getPrimaryTeamName(provider) ?? '').toLowerCase();
      return name.includes(q) || team.includes(q);
    });
  }

  getPrimaryTeamName(patientProvider: PatientProvider): string {
    return patientProvider.teams.find(team => team.isPrimary)?.name || '';
  }

  onClearSearch(inputEl: HTMLInputElement) {
    this.searchText.set('');
    queueMicrotask(() => inputEl.focus());
  }

  select(providerUserId: string) {
    this.selectedProviderUserId.set(providerUserId);
  }

  onRowKeydown(event: KeyboardEvent, providerUserId: string) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.onProviderActivate(providerUserId);
    }
  }

  onProviderActivate(providerUserId: string) {
    if (this.isMobileBrowser()) {
      this.select(providerUserId);
    } else {
      this.providerUserIdSelected.emit(providerUserId);
    }
  }

  onCancel() {
    this.selectedProviderUserId.set(null);
    this.searchText.set('');
    this.location.back();
  }

  onContinue() {
    const providerUserId = this.selectedProviderUserId();
    if (!providerUserId) return;

    if (this.isMobileBrowser()) {
      this.store.dispatch(
        loadPageWithPatientIdParameter({ route: `appointments/schedule/${providerUserId}/visit-type` })
      );
    } else {
      this.providerUserIdSelected.emit(providerUserId);
    }
  }

  getEmptyStateCopy(): string {
    return this.searchText().trim().length > 0 ? 'No providers match your search' : 'No care team members';
  }

  getEmptyStateSubtitle(): string {
    return this.searchText().trim().length > 0
      ? 'Try a different name or specialty to find a provider.'
      : 'Your care team members will appear here when available.';
  }

  getEmptyStateHint(): string {
    return this.searchText().trim().length > 0
      ? 'Clear or change your search to see more results.'
      : 'If you need help now, you can contact your care team.';
  }
}
